import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { createCustomer, updateCustomer, uploadFile, sendWhatsappMessage, uploadContractPdf } from "@/integrations/supabase/services";
import CustomerPhotoCapture from "@/components/customers/CustomerPhotoCapture";
import RequireLabel from '@/components/common/RequireLabel';
import { Loader2 } from "lucide-react";
import { Customer } from "@/types/models";
import { formatPhoneInput } from "@/lib/utils";
import { generateContractPdf } from "@/lib/contractGenerator";

interface AddCustomerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (customer: Customer) => void;
  customerToEdit?: Customer | null;
}

const AddCustomerModal = ({ open, onOpenChange, onSuccess, customerToEdit }: AddCustomerModalProps) => {
  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [idPhotoUrl, setIdPhotoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const isEditMode = Boolean(customerToEdit);

  useEffect(() => {
    if (open) {
      if (isEditMode && customerToEdit) {
        setCustomerId(customerToEdit.id_card?.toString() || "");
        setCustomerName(customerToEdit.name);
        setCustomerPhone(customerToEdit.phone);
        setCustomerEmail(customerToEdit.email || null);
        setProfilePhotoUrl(customerToEdit.profile_photo_url || null);
        setIdPhotoUrl(customerToEdit.id_photo_url || null);
      } else {
        setCustomerId("");
        setCustomerName("");
        setCustomerPhone("");
        setCustomerEmail(null);
        setProfilePhotoUrl(null);
        setIdPhotoUrl(null);
      }
    }
  }, [customerToEdit, isEditMode, open]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneInput(e.target.value);
    if (formatted !== undefined) {
      setCustomerPhone(formatted);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customerId || !customerName || !customerPhone || !profilePhotoUrl || !idPhotoUrl) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Debe llenar todos los campos requeridos, incluyendo la foto de perfil y de cédula.",
      });
      return;
    }

    setIsLoading(true);

    let formattedPhone = customerPhone.trim();
    if (!formattedPhone.startsWith('+58')) {
      formattedPhone = `+58${formattedPhone.replace(/\D/g, '')}`;
    }
    
    try {
      let uploadedIdPhotoUrl = customerToEdit?.id_photo_url || "";
      if (idPhotoUrl && idPhotoUrl !== customerToEdit?.id_photo_url) {
        const idFileName = `customer-id-${customerId}-${Date.now()}.jpg`;
        uploadedIdPhotoUrl = await uploadFile(idPhotoUrl, idFileName);
      }

      let uploadedProfilePhotoUrl = customerToEdit?.profile_photo_url || "";
      if (profilePhotoUrl && profilePhotoUrl !== customerToEdit?.profile_photo_url) {
        const profileFileName = `customer-profile-${customerId}-${Date.now()}.jpg`;
        uploadedProfilePhotoUrl = await uploadFile(profilePhotoUrl, profileFileName);
      }

      // Generar y subir el contrato PDF personalizado
      let finalContractUrl = customerToEdit?.contract_url || "";
      const hasDataChanged = !customerToEdit || 
        customerToEdit.name !== customerName || 
        customerToEdit.id_card?.toString() !== customerId || 
        customerToEdit.id_photo_url !== uploadedIdPhotoUrl;

      if (hasDataChanged && uploadedIdPhotoUrl) {
        try {
          const contractBlob = await generateContractPdf({
            name: customerName,
            idCard: parseInt(customerId),
            phone: formattedPhone,
            idPhotoUrl: uploadedIdPhotoUrl,
          });
          const contractFileName = `contracts/contract-${customerId}-${Date.now()}.pdf`;
          finalContractUrl = await uploadContractPdf(contractBlob, contractFileName);
        } catch (contractErr) {
          console.error("Error al generar el contrato PDF:", contractErr);
          toast({
            variant: "destructive",
            title: "Advertencia",
            description: "No se pudo generar el PDF del contrato, pero el cliente se guardará.",
          });
        }
      }

      const customerData = {
        id_card: parseInt(customerId),
        name: customerName,
        phone: formattedPhone,
        email: customerEmail,
        id_photo_url: uploadedIdPhotoUrl,
        profile_photo_url: uploadedProfilePhotoUrl,
        contract_url: finalContractUrl,
      };

      if (isEditMode && customerToEdit) {
        const updatedCustomer = await updateCustomer(customerToEdit.id, customerData);
        onSuccess(updatedCustomer);
        toast({
          title: "Cliente actualizado",
          description: "El cliente se ha actualizado exitosamente",
        });
      } else {
        const newCustomer = await createCustomer(customerData);
        onSuccess(newCustomer);
        
        // Enviar el contrato por WhatsApp de manera automática al registrar
        sendWhatsappMessage({
          cardID: newCustomer.id_card, 
          template: "welcome", 
          variables: [newCustomer.name], 
          contract: true,
          contractUrl: finalContractUrl || undefined
        }).catch(err => console.error("Error en envío de WhatsApp:", err));

        toast({
          title: "Cliente agregado",
          description: "El cliente se ha registrado exitosamente y se ha enviado el contrato por WhatsApp",
        });
      }
      onOpenChange(false);
    } catch (error: unknown) {
      console.error('Error processing customer:', error);
      let errorMessage = "Ocurrió un error inesperado";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      if (errorMessage.includes('duplicate key') || errorMessage.includes('UK_id_card')) {
        toast({
          title: "Cliente existente",
          description: "Un cliente con esta cédula ya existe en el sistema.",
          className: "bg-orange-500 text-white border-orange-600",
        });
      } else {
        toast({
          variant: "destructive",
          title: isEditMode ? "Error al actualizar cliente" : "Error al registrar cliente",
          description: "Ocurrió un error al procesar el cliente, contacte al administrador.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Editar Cliente" : "Agregar Nuevo Cliente"}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <RequireLabel htmlFor="cedula">Cédula</RequireLabel>
            <Input
              id="cedula"
              placeholder="Número de Cédula"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              required
              disabled={isEditMode}
            />
          </div>
          
          <div>
            <RequireLabel htmlFor="nombre">Nombre</RequireLabel>
            <Input
              id="nombre"
              placeholder="Nombre Completo"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              required
            />
          </div>
          
          <div>
            <RequireLabel htmlFor="telefono">Teléfono</RequireLabel>
            <Input
              id="telefono"
              placeholder="+58..."
              value={customerPhone}
              onChange={handlePhoneChange}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="email">Correo Electrónico</Label>
            <Input
              id="email"
              type="email"
              placeholder="correo@ejemplo.com"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
            />
          </div>

          <div>
            <CustomerPhotoCapture
              label="Foto de Perfil"
              onCapture={(imageSrc) => setProfilePhotoUrl(imageSrc)}
              currentImage={profilePhotoUrl}
              required
            />
          </div>

          <div>
            <CustomerPhotoCapture
              label="Foto de Cédula"
              onCapture={(imageSrc) => setIdPhotoUrl(imageSrc)}
              currentImage={idPhotoUrl}
              required
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditMode ? "Actualizar" : "Registrar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomerModal;
